import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authDepth, leaveAuth, withReturn } from '../utils/authReturn';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiArrowLeft, FiPhone } from 'react-icons/fi';
import supabase from '../services/supabase';
import CountryCodePicker from '../components/CountryCodePicker';
import usePhoneCountry from '../hooks/usePhoneCountry';
import useCountryNudge, { CountryCodeHint } from '../hooks/useCountryNudge';
import SocialSignInButtons from '../components/SocialSignInButtons';
import { signInWithPhone } from '../services/authCodes';
import { track } from '../services/track';
import { EV } from '../shared/analyticsEvents';
import '../styles/Auth.scss';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo || null;
  // How many sign-in pages back the page that sent them is (utils/authReturn.js).
  const depth = authDepth(location);
  const prefillPhone = location.state?.phone || '';
  // A country code handed over with the number (from the RSVP popup) is
  // kept; otherwise the picker starts on the visitor's own country.
  const prefillCountryCode = location.state?.countryCode;

  const [loginMethod, setLoginMethod] = useState(prefillPhone ? 'phone' : 'email');
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(prefillPhone);
  const [phonePassword, setPhonePassword] = useState('');
  const [selectedCountry, setSelectedCountry] = usePhoneCountry(prefillCountryCode);
  const countryNudge = useCountryNudge(selectedCountry);
const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (signInError) throw signInError;

      track(EV.LOGIN_COMPLETED, { method: 'email' });
      if (data?.user) {
        if (redirectTo) {
          navigate(redirectTo, { replace: true });
          return;
        }
        // Check if profile is complete
        const { data: profile } = await supabase
          .from('users')
          .select('first_name, last_name, username')
          .eq('id', data.user.id)
          .single();

        if (profile && profile.first_name && profile.last_name) {
          if (profile.username) {
            navigate(`/${profile.username}`);
          } else {
            navigate('/home');
          }
        } else {
          navigate('/profile-setup', { state: withReturn(redirectTo) });
        }
      }
    } catch (error) {
      track(EV.LOGIN_FAILED, { method: 'email', code: error.message?.includes('Invalid login credentials') ? 'invalid_credentials' : 'error' });
      console.error('Login error:', error);

      if (error.message?.includes('Invalid login credentials')) {
        setError('Invalid email or password');
      } else {
        setError(error.message || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    
    // No country detected or picked: ask for it, rather than guess one.
    if (!countryNudge.check()) return;
    if (!phoneNumber.trim() || !phonePassword) {
      setError('Please enter your phone number and password');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Checked by the server (auth-codes phone_login), which returns a
      // one-time token for the session. This used to read the account's email
      // from `users` by phone number and sign in with it - which failed for
      // accounts made with a phone, and told anyone whose email sat behind a
      // number.
      const phone = `${selectedCountry.code.replace(/\D/g, '')}${phoneNumber.replace(/\D/g, '').replace(/^0+/, '')}`;
      const data = await signInWithPhone(phone, phonePassword);

      track(EV.LOGIN_COMPLETED, { method: 'phone' });
      if (data?.user) {
        if (redirectTo) {
          navigate(redirectTo, { replace: true });
          return;
        }
        const { data: profile } = await supabase
          .from('users')
          .select('first_name, last_name, username')
          .eq('id', data.user.id)
          .single();

        if (profile && profile.first_name && profile.last_name) {
          if (profile.username) {
            navigate(`/${profile.username}`);
          } else {
            navigate('/home');
          }
        } else {
          navigate('/profile-setup', { state: withReturn(redirectTo) });
        }
      }
    } catch (error) {
      track(EV.LOGIN_FAILED, { method: 'phone', code: error.code || 'error' });
      console.error('Login error:', error);

      if (error.message?.includes('Invalid login credentials') ||
          error.message?.includes('No account found')) {
        setError('Invalid phone number or password');
      } else {
        setError(error.message || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <img src="/images/events/6.jpg" alt="Background" />
        <div className="auth-overlay"></div>
      </div>

      <div className="auth-content">
        <motion.div
          className="auth-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back beside the title rather than in a row of its own above the
              card, so Google sign-in is on screen without scrolling on a
              phone. Back goes to what they were doing (utils/authReturn.js). */}
          <div className="auth-head">
            <button type="button" className="back-button" aria-label="Back"
              onClick={() => leaveAuth(navigate, redirectTo, depth)}>
              <FiArrowLeft size={20} />
            </button>
            <h1 className="auth-title">Welcome back!</h1>
          </div>
          {/* The note said "to complete your RSVP" for every redirect there
              is. Most of them are not RSVPs any more — the commonest is now
              publishing an invitation someone has already built. What every
              case has in common is the second half, so that is what it says. */}
          {redirectTo && (
            <p className="auth-redirect-note">
              {(redirectTo.startsWith('/create-invitation') || redirectTo.startsWith('/create-event'))
                ? 'Sign in to publish — your invitation is saved and waiting.'
                : redirectTo.startsWith('/create-greeting-card')
                  ? 'Sign in to make your card — you will be taken right back.'
                  : "Sign in to carry on — you'll be taken right back."}
            </p>
          )}

          {/* Google first: one tap, and the commonest way in. */}
          <SocialSignInButtons
            placement="top"
            onSignedIn={(r) => {
              if (redirectTo && r?.profile_complete !== false) navigate(redirectTo, { replace: true });
              else if (r?.profile_complete) navigate('/home');
              else navigate('/profile-setup', { state: withReturn(redirectTo) });
            }}
          />

          <div className="auth-tabs">
            <button
              className={`tab ${loginMethod === 'email' ? 'active' : ''}`}
              onClick={() => {
                setLoginMethod('email');
                setError(null);
              }}
            >
              Email
            </button>
            <button
              className={`tab ${loginMethod === 'phone' ? 'active' : ''}`}
              onClick={() => {
                setLoginMethod('phone');
                setError(null);
              }}
            >
              Phone
            </button>
          </div>

          {loginMethod === 'email' ? (
            <form onSubmit={handleEmailLogin}>
              <div className="input-group">
                <label>Email</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
              </div>

              <div className="input-group">
                <label>Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon" />
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
              </div>

              {error && <p className="error-text">{error}</p>}

              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || !email.trim() || !password}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handlePhoneLogin}>
              <div className="input-group">
                <label>Phone Number</label>
                <div className="phone-input-wrapper">
                  <CountryCodePicker
                    value={selectedCountry}
                    onChange={setSelectedCountry}
                    nudge={countryNudge}
                  />
                  <div className="input-with-icon flex-1">
                    <FiPhone className="input-icon" />
                    <input
                      type="tel"
                      placeholder="Enter phone number"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''));
                        setError(null);
                      }}
                    />
                  </div>
                </div>
                <CountryCodeHint nudge={countryNudge} />
</div>

              <div className="input-group">
                <label>Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon" />
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={phonePassword}
                    onChange={(e) => {
                      setPhonePassword(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
              </div>

              {error && <p className="error-text">{error}</p>}

              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || !phoneNumber.trim() || !phonePassword}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          <div className="auth-footer">
            <p>Don't have an account? <Link to="/signup" state={withReturn(redirectTo, depth ? depth + 1 : 0)}>Sign Up</Link></p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;